import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";

import { NAV, ACCOUNT_NAV, MOBILE_PRIMARY, MOBILE_MORE } from "../../lib/site";
import { useAuth } from "../../hooks/useAuth";

/**
 * The phone's navigation (business/20 §65–69; business/19 §75): a bottom bar with
 * Home · Purchases · Properties · Requests · More, reachable one-handed. "More" opens a drawer holding Documents,
 * Notifications, Profile, Security and Logout. Tablet and desktop use the Sidebar instead.
 */
// ── CHOSEN, NOT SLICED (Part 4 §33, kept) ──────────────────────────────────
// The bar names its destinations by path, so re-ordering the sidebar never silently re-orders the phone's bar.
const PRIMARY = MOBILE_PRIMARY.map((to) => NAV.find((item) => item.to === to)).filter(Boolean);
const MORE = MOBILE_MORE.map((to) => NAV.find((item) => item.to === to)).filter(Boolean);

export function BottomBar({ moreOpen, onOpenMore }) {
  return (
    <nav className="vp-bottombar d-md-none" aria-label="Primary">
      {PRIMARY.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          className={({ isActive }) => `vp-tab${isActive ? " active" : ""}`}
        >
          <i className={`bi ${item.icon}`} aria-hidden="true" />
          <span>{item.short || item.label}</span>
        </NavLink>
      ))}
      <button type="button" id="vp-more-button" className="vp-tab btn btn-link" onClick={onOpenMore}
        aria-haspopup="dialog" aria-expanded={moreOpen} aria-controls="vp-more">
        <i className="bi bi-three-dots" aria-hidden="true" />
        <span>More</span>
      </button>
    </nav>
  );
}

/** The "More" drawer: a modal dialog — focus moves in, Escape and the backdrop close it, focus returns to More. */
export function Drawer({ open, onClose }) {
  const { signOut } = useAuth();
  const panel = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.getElementById("vp-more-button");
    const first = panel.current && panel.current.querySelector("a, button");
    if (first) first.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !panel.current) return;
      const f = [...panel.current.querySelectorAll("a, button")];
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); if (opener) opener.focus(); };
  }, [open, onClose]);

  if (!open) return null;
  const link = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === "/"}
      onClick={onClose}
      className={({ isActive }) => `vp-drawerlink${isActive ? " active" : ""}`}
    >
      <i className={`bi ${item.icon}`} aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );
  return (
    <>
      <div className="vp-backdrop d-md-none" onClick={onClose} aria-hidden="true" />
      <aside id="vp-more" ref={panel} className="vp-drawer d-md-none" role="dialog" aria-modal="true" aria-labelledby="vp-more-title">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 id="vp-more-title" className="h6 mb-0">More</h2>
          <button type="button" className="btn btn-sm btn-light" onClick={onClose} aria-label="Close menu">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="More">
          {MORE.map(link)}
          <hr className="my-2" />
          {ACCOUNT_NAV.map(link)}
          <button type="button" className="vp-drawerlink btn btn-link w-100 text-start" onClick={() => { onClose(); signOut(); }}>
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
            <span>Logout</span>
          </button>
        </nav>
      </aside>
    </>
  );
}
